<template>
    <VTextField
        v-model="displayVal"
        :variant="variant"
        :label="label"
        :density="density"
        :hint="hint"
        :prefix="prefix"
        :suffix="suffix"
        :messages="messages"
        :disabled="disabled"
        :clearable="clearable"
        :readonly="readonly"
        :loading="loading"
        :errorMessages="errorMessages"
        v-maska="mask"
        :placeholder="placeholder"
        @blur="onBlur" />
</template>
<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { defineComponent, ref, PropType, watch } from 'vue';
    import { VTextField } from 'vuetify/components';
    import { Variant, Density } from './Time.interfaces';
    import { getTranslatedDatetimeFormat } from '@shared/helpers/datetime/datetime.formatters';
    import { MaskInputOptions } from 'maska';
    import { isValidTime } from '@shared/helpers/datetime/datetime';

    export default defineComponent({
        name: 'Time',
        methods: { t },
        components: { VTextField },
        emits: ['update:modelValue'],
        props: {
            modelValue: { type: String },
            label: String,
            hint: String,
            prefix: String,
            suffix: String,
            loading: { type: Boolean as PropType<boolean>, default: false },
            disabled: { type: Boolean as PropType<boolean>, default: false },
            clearable: { type: Boolean as PropType<boolean>, default: false },
            readonly: { type: Boolean as PropType<boolean>, default: false },
            messages: { type: Array as PropType<string[]> },
            errorMessages: {
                type: [Array, String] as PropType<string[] | string>,
            },
            variant: { type: String as PropType<Variant>, default: 'outlined' },
            density: { type: String as PropType<Density>, default: 'default' },
        },
        setup(props, { emit }) {
            const format = 'HH:mm';
            const placeholder = getTranslatedDatetimeFormat(format);
            const displayVal = ref<string | undefined>();
            const mask = ref<MaskInputOptions>({
                mask: format.replace(/[a-zA-Zå]/g, '#'),
                eager: true,
            });

            const onBlur = () => {
                onUpdateTime(displayVal.value);
            };

            const onUpdateTime = (time?: string) => {
                if (time && isValidTime(time)) {
                    emit('update:modelValue', time);
                } else {
                    emit('update:modelValue', undefined);
                }
            };

            watch(
                () => displayVal.value,
                (newValue) => {
                    onUpdateTime(newValue);
                }
            );

            watch(
                () => props.modelValue,
                (newValue, prevValue) => {
                    if (newValue && newValue !== prevValue) {
                        displayVal.value = newValue;
                    }
                },
                { immediate: true }
            );

            return {
                displayVal,
                onBlur,
                mask,
                placeholder,
            };
        },
    });
</script>
